import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { User, Building2, ArrowRight, BarChart3, CheckCircle } from "lucide-react";

import { API } from "../lib/api";

const PATHS = [
  {
    id: "individual",
    icon: User,
    title: "Individual",
    subtitle: "I'm assessing my own projects",
    features: [
      "Personal SYNQ assessments",
      "AI-powered insights & action plans",
      "What-If simulation",
      "Weekly pulse checks",
    ],
    cta: "Start as Individual",
    accent: "blue",
  },
  {
    id: "organization",
    icon: Building2,
    title: "Organization",
    subtitle: "I'm managing a team or company",
    features: [
      "Everything in Individual, plus:",
      "Import & manage employees",
      "Role-based assessments (IC / Manager / Executive)",
      "Team & org-wide dashboards",
      "Alert system & action tracking",
    ],
    cta: "Set Up Organization",
    accent: "slate",
  },
];

export default function ChoosePathPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChoose = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/account/choose`, { account_type: selected }, { withCredentials: true });
      if (refreshUser) await refreshUser();
      if (selected === "organization") {
        navigate("/admin/setup");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center">
          <div className="flex items-center gap-2.5">
            <img src="/synq-mark.png" alt="SYNQ" className="w-8 h-8 object-contain" draggable={false} />
            <span className="font-outfit text-lg font-black text-slate-900">SYNQ</span>
            <span className="font-ibm-plex text-xs text-slate-400">Project IQ</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-10 animate-slide-up">
            <h1 className="font-outfit text-3xl sm:text-4xl font-black text-slate-900 mb-2">
              How will you use SYNQ?
            </h1>
            <p className="font-ibm-plex text-base text-slate-500">
              Choose your path. You can always switch later.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8 animate-slide-up stagger-1">
            {PATHS.map((path) => {
              const Icon = path.icon;
              const isSelected = selected === path.id;
              return (
                <button
                  key={path.id}
                  data-testid={`choose-${path.id}`}
                  onClick={() => setSelected(path.id)}
                  className={`text-left rounded-xl border-2 p-6 transition-all ${
                    isSelected
                      ? "border-slate-900 bg-white shadow-md"
                      : "border-gray-200 bg-white hover:border-slate-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isSelected ? "bg-slate-900 text-white" : "bg-gray-100 text-slate-500"
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? "border-slate-900 bg-slate-900" : "border-gray-300"
                    }`}>
                      {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  <h2 className="font-outfit text-xl font-bold text-slate-900 mb-1">{path.title}</h2>
                  <p className="font-ibm-plex text-sm text-slate-500 mb-4">{path.subtitle}</p>
                  <ul className="space-y-2">
                    {path.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? "text-emerald-500" : "text-slate-300"}`} />
                        <span className="font-ibm-plex text-sm text-slate-600">{f}</span>
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          <div className="text-center animate-slide-up stagger-2">
            <button
              data-testid="confirm-path-btn"
              onClick={handleChoose}
              disabled={!selected || loading}
              className={`font-outfit font-semibold px-8 py-3.5 rounded-lg transition-colors text-base flex items-center gap-2 mx-auto ${
                selected
                  ? "bg-slate-900 text-white hover:bg-slate-800"
                  : "bg-gray-200 text-slate-400 cursor-not-allowed"
              } disabled:opacity-60`}
            >
              {loading ? "Setting up..." : selected === "organization" ? "Set Up Organization" : selected === "individual" ? "Continue as Individual" : "Choose a path"}
              {!loading && selected && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
